/**
 * Anivaze v4 — Frame & aspect-agnostic layout system.
 *
 * v3 hardcoded an 800×450 landscape canvas and centre math. v4 authors every
 * scene ONCE against a safe-area layout and reflows it to 9:16 / 1:1 / 16:9.
 * A scene never reads raw pixels — it reads the `SceneLayout` computed here, so
 * the same composition resolves correctly tall, square, or wide.
 *
 * See design-plan/v4/PLAN.md §4.
 */

export type AspectId = "9:16" | "1:1" | "16:9";
export const ASPECTS: AspectId[] = ["9:16", "1:1", "16:9"];

export interface Frame {
  w: number;
  h: number;
  aspect: AspectId;
  /** Margins (in frame px) reserved for platform UI / captions / hook. */
  safe: { top: number; right: number; bottom: number; left: number };
}

/** A rectangle plus its centre — the unit every layout helper returns. */
export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

const box = (x: number, y: number, w: number, h: number): Box => ({
  x,
  y,
  w,
  h,
  cx: x + w / 2,
  cy: y + h / 2,
});

/**
 * Frame presets. 9:16 reserves a tall bottom band for captions and a top band
 * for the platform action-rail + hook; 16:9 is wide with slim margins.
 */
export const FRAME_PRESETS: Record<AspectId, Frame> = {
  "9:16": { w: 1080, h: 1920, aspect: "9:16", safe: { top: 210, right: 72, bottom: 380, left: 72 } },
  // bottom was 230 (21% of frame height) — copied from the 9:16 proportion, but a
  // square feed post has no bottom-nav/action-rail chrome overlaid on it the way a
  // full-bleed vertical Reel does, so that margin only starved `content` of room
  // (hero collided with a 2-line headline; see frame.test.ts + Stage.test.tsx).
  // 120 matches 16:9's ~14% and gives the hero focal point real clearance.
  "1:1": { w: 1080, h: 1080, aspect: "1:1", safe: { top: 96, right: 80, bottom: 120, left: 80 } },
  // bottom slimmed 150→96 so the (square-ish) hero visual gets real height on a
  // wide frame instead of being pillar-boxed tiny in the middle.
  "16:9": { w: 1920, h: 1080, aspect: "16:9", safe: { top: 84, right: 120, bottom: 96, left: 120 } },
};

export const getFrame = (a: AspectId): Frame => FRAME_PRESETS[a];

export interface SceneLayout {
  frame: Frame;
  /** The whole frame. */
  full: Box;
  /** Safe content area (frame minus safe margins). */
  safe: Box;
  /** Caption band — the bottom slice of the safe area. */
  caption: Box;
  /** Main visual area — safe area above the caption band. */
  content: Box;
  /** Focal point for the primary visual (biased low on tall frames). */
  hero: { cx: number; cy: number };
  /**
   * One responsive layout unit. Based on the SHORT side so type/geometry stay
   * the same physical size across aspects (wide frames just get more room),
   * rather than ballooning on 16:9. All presets ⇒ unit = 10.8.
   */
  unit: number;
}

/** Frame → the layout every scene lays itself out against. */
export function computeLayout(frame: Frame): SceneLayout {
  const full = box(0, 0, frame.w, frame.h);

  const sx = frame.safe.left;
  const sy = frame.safe.top;
  const sw = frame.w - frame.safe.left - frame.safe.right;
  const sh = frame.h - frame.safe.top - frame.safe.bottom;
  const safe = box(sx, sy, sw, sh);

  // Caption band: bottom ~32% of the safe area, capped so wide frames stay slim.
  const capH = Math.min(sh * 0.32, frame.aspect === "16:9" ? 150 : 360);
  const gap = frame.h * 0.02;
  const caption = box(sx, sy + sh - capH, sw, capH);
  const content = box(sx, sy, sw, sh - capH - gap);

  // Bias fraction down content.h. 9:16 has tall, roomy content so the hero can
  // sit lower without crowding the headline above it. 1:1's content band is
  // shorter in absolute terms, so a flat 0.52 pulls the hero up into a 2-line
  // headline's second line — 0.66 gives it the same real clearance instead.
  const heroFrac = frame.aspect === "9:16" ? 0.58 : frame.aspect === "1:1" ? 0.66 : 0.52;
  const hero = {
    cx: content.cx,
    cy: content.y + content.h * heroFrac,
  };

  const unit = Math.min(frame.w, frame.h) / 100;

  return { frame, full, safe, caption, content, hero, unit };
}
